import type { TaskState } from "./states";

/**
 * The API is same-origin (apps/app serves this bundle), so every path is
 * relative and no CORS or base URL is involved.
 *
 * A 401 means the session cookie is missing or expired. There is nothing useful
 * to render in that case, so we bounce straight to Slack sign-in rather than
 * teach every caller to handle it.
 *
 * Types are written BY HAND against apps/app/src/routes/api.ts — the
 * dashboard deliberately imports no @barista/* package (see CLAUDE.md's
 * moduleResolution/zod exception). Runtime vocabulary (states, transitions,
 * models) comes from GET /api/meta, so only SHAPES live here, never lists.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function handle<T>(res: Response): Promise<T> {
  if (res.status === 401) {
    window.location.href = "/auth/slack";
    // The redirect is async; block rather than let a caller see a bad shape.
    return new Promise<never>(() => {});
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(res.status, body.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

async function get<T>(path: string): Promise<T> {
  return handle(await fetch(`/api${path}`, { credentials: "same-origin" }));
}

async function post<T>(path: string, body?: unknown): Promise<T> {
  return handle(
    await fetch(`/api${path}`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  );
}

export interface Meta {
  states: TaskState[];
  triggers: string[];
  transitions: Partial<Record<TaskState, Partial<Record<string, TaskState>>>>;
  runnerModes: string[];
  dispatchEntryTriggers: Record<string, string>;
  models: { alias: string; label: string; hint: string }[];
  taskEventTypes: string[];
  transcriptEventTypes: string[];
}

export interface TaskRepo {
  repoId: string;
  repoFullName: string | null;
  branchName: string | null;
  pushedAt: string | null;
  prNumber: number | null;
  prUrl: string | null;
  prState: "none" | "open" | "draft" | "merged" | "closed";
  reviewRequired: boolean;
  sensitiveHits: string[];
}

export interface Task {
  id: string;
  conversationId: string;
  title: string;
  state: TaskState;
  kind: "implement" | "explore";
  sourceKind: "github_issue" | "freeform" | "scheduled";
  repos: TaskRepo[];
  profile: string | null;
  /** LiteLLM alias picked on the confirm card; null = the profile's default. */
  model: string | null;
  plannerModel: string | null;
  attempt: number;
  issueUrl: string | null;
  requestedBySlackUserId: string;
  spentUsd: number;
  budgetUsd: number;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Conversation {
  id: string;
  title: string;
  origin: "slack" | "dashboard" | "scheduled";
  createdBySlackUserId: string;
  hasSlackThread: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ConversationListItem extends Conversation {
  tasks: Task[];
}

export interface Message {
  id: string;
  taskId: string | null;
  kind: "user" | "steering" | "agent_report" | "milestone" | "system";
  status: "posted" | "pending" | "consumed";
  source: string;
  authorSlackUserId: string | null;
  body: string;
  payload: unknown;
  createdAt: string;
}

export interface Draft {
  id: string;
  title: string;
  kind: "implement" | "explore" | "review";
  spec: string | null;
  issueUrl: string | null;
  repoIds: string[];
  model: string | null;
  plannerModel: string | null;
  expiresAt: string;
}

export interface ConversationDetail {
  conversation: Conversation;
  messages: Message[];
  tasks: Task[];
  drafts: Draft[];
  totalSpendUsd: number;
}

export interface TaskEvent {
  seq: number;
  taskId: string;
  type: string;
  payload: unknown;
  createdAt: string;
}

export interface TranscriptEntry {
  seq: number;
  taskId: string;
  runNumber: number;
  type: string;
  content: Record<string, unknown>;
  createdAt: string;
}

export interface Overview {
  live: Task[];
  countsByState: Record<string, number>;
  spend: { todayUsd: number; dailyCapUsd: number };
  killSwitch: boolean;
  maxConcurrentTasks: number;
}

export interface Metrics {
  completed: number;
  failed: number;
  cancelled: number;
  successRate: number | null;
  mergedPrCount: number;
  costPerMergedPrUsd: number | null;
  spentWindowUsd: number;
  days: number;
  window: string;
  spendScope: string;
}

export interface Session {
  id: string;
  runNumber: number;
  mode: string;
  model: string;
  numTurns: number | null;
  costUsd: number | null;
  outcome: string | null;
  hasArtifacts: boolean;
  startedAt: string | null;
  endedAt: string | null;
}

export interface Approval {
  id: string;
  kind: string;
  status: string;
  payload: unknown;
  decidedBySlackUserId: string | null;
  decidedAt: string | null;
  expiresAt: string;
  createdAt: string;
}

export interface BlockingLock {
  repoId: string;
  repoFullName: string | null;
  heldByTaskId: string;
}

export interface TaskDetail {
  task: Task;
  brief: string | null;
  blockingLocks: BlockingLock[];
  sessions: Session[];
  approvals: Approval[];
}

export interface RepoOption {
  id: string;
  fullName: string;
  defaultBranch: string;
  notes: string | null;
}

export interface Runner {
  id: string;
  engines: string[];
  version: string | null;
  host: string | null;
  capabilities: string[];
  currentTaskId: string | null;
  lastSeenAt: string;
  online: boolean;
}

export interface PendingJob {
  taskId: string;
  engine: string;
  requiredCapabilities: string[];
  createdAt: string;
  serviceable: boolean;
}

export interface Lock {
  repoId: string;
  repoFullName: string | null;
  taskId: string;
  taskTitle: string | null;
  taskState: TaskState | null;
  acquiredAt: string;
}

export interface Costs {
  days: number;
  daily: {
    day: string;
    costUsd: number;
    inputTokens: number;
    outputTokens: number;
  }[];
  byModel: { model: string; source: string; costUsd: number }[];
}

export const api = {
  meta: () => get<Meta>("/meta"),
  overview: () => get<Overview>("/overview"),
  metrics: (days: number) => get<Metrics>(`/metrics?days=${days}`),
  costs: (days: number) => get<Costs>(`/costs?days=${days}`),

  conversations: (cursor?: string) =>
    get<{ conversations: ConversationListItem[]; nextCursor: string | null }>(
      `/conversations${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
    ),
  conversation: (id: string) => get<ConversationDetail>(`/conversations/${id}`),

  task: (id: string) => get<TaskDetail>(`/tasks/${id}`),
  taskEvents: (id: string, after: number) =>
    get<{ events: TaskEvent[]; cursor: number | null }>(
      `/tasks/${id}/events?after=${after}`,
    ),
  taskTranscript: (id: string, after: number) =>
    get<{ entries: TranscriptEntry[]; cursor: number | null }>(
      `/tasks/${id}/transcript?after=${after}&limit=500`,
    ),
  runArtifacts: (taskId: string, runNumber: number) =>
    get<{ url: string }>(`/tasks/${taskId}/runs/${runNumber}/artifacts`),

  repos: () => get<{ repos: RepoOption[] }>("/repos"),
  runners: () =>
    get<{ runners: Runner[]; pendingJobs: PendingJob[] }>("/runners"),
  locks: () => get<{ locks: Lock[] }>("/locks"),

  createAsk: (body: { text: string; intent: "implement" | "explore" }) =>
    post<{ conversationId: string }>("/asks", body),
  confirmDraft: (
    id: string,
    body: {
      repoIds?: string[];
      model?: string;
      plannerModel?: string | null;
    },
  ) => post<{ ok: true; taskId: string | null }>(`/drafts/${id}/confirm`, body),
  cancelDraft: (id: string) => post<{ ok: true }>(`/drafts/${id}/cancel`),
  sendMessage: (taskId: string, body: string) =>
    post<{ ok: true; messageId: string }>(`/tasks/${taskId}/messages`, {
      body,
    }),
  cancelTask: (id: string) => post<{ ok: true }>(`/tasks/${id}/cancel`),
  requestReview: (id: string) =>
    post<{ ok: true; draftId: string }>(`/tasks/${id}/review`),
  decideApproval: (id: string, decision: "approved" | "denied") =>
    post<{ ok: true }>(`/approvals/${id}/decide`, { decision }),
};
